/**
 * Graph-color token accessors for reporting / data-visualization surfaces.
 *
 * The design system exposes 10 semantic graph color tokens
 * (`--color-graph-primary` → `--color-graph-denary`) in
 * `packages/shared/src/styles/theme.css` and
 * `packages/config/tailwind/theme.ts`.
 *
 * Each series is assigned a deterministic slot from this palette so the
 * same series reads with the same color everywhere it appears (stacked
 * bar, area line, and any legend).
 *
 * Recharts accepts arbitrary CSS color strings as its `stroke` / `fill`
 * props, so the simplest, most correct wiring is to pass the raw
 * `rgb(var(--color-graph-n))` expression straight through — the browser
 * resolves it at paint time and a theme-switch picks up the new token
 * value automatically.
 *
 * Lives in `shared/utils` (not a component folder) because it is a pure
 * token accessor with no React surface; it is consumed by the shared
 * Charts components and by app-level reporting pages alike.
 */

/** Ordered slots of the graph palette. */
const GRAPH_SLOTS = [
    "primary",
    "secondary",
    "tertiary",
    "quaternary",
    "quinary",
    "senary",
    "septenary",
    "octonary",
    "nonary",
    "denary"
] as const;

export type GraphSlot = (typeof GRAPH_SLOTS)[number];

/** CSS custom property name for a given palette slot. */
export function graphTokenVar(slot: GraphSlot): string {
    return `--color-graph-${slot}`;
}

/**
 * CSS color expression Recharts can consume as `stroke` / `fill`.
 * Keeps the value in CSS-var space so the palette stays theme-driven.
 */
export function graphColor(slot: GraphSlot): string {
    return `rgb(var(--color-graph-${slot}))`;
}

/** Same as `graphColor` but with an alpha multiplier (0–1). */
export function graphColorAlpha(slot: GraphSlot, alpha: number): string {
    return `rgb(var(--color-graph-${slot}) / ${alpha})`;
}

/**
 * Static slot → Tailwind background-class lookup. Use this when a swatch's
 * color is determined by a `GraphSlot` enum value rather than a runtime
 * color string — lets the consumer drop `style={{ backgroundColor }}` for
 * a plain `className` lookup. The class names match the `graph` palette
 * registered in `packages/config/tailwind/theme.ts`.
 */
export const GRAPH_BG_CLASS: Record<GraphSlot, string> = {
    primary: "bg-graph-primary",
    secondary: "bg-graph-secondary",
    tertiary: "bg-graph-tertiary",
    quaternary: "bg-graph-quaternary",
    quinary: "bg-graph-quinary",
    senary: "bg-graph-senary",
    septenary: "bg-graph-septenary",
    octonary: "bg-graph-octonary",
    nonary: "bg-graph-nonary",
    denary: "bg-graph-denary"
};

/**
 * Deterministic slot for a given series-list index, wrapping at 10.
 *
 * Reversed order — the first series gets the deepest slot (denary),
 * subsequent series step toward lighter slots. The palette runs
 * light → mixed in token order, which meant the anchor series was
 * rendering on the palest hue; reversing puts visual weight on the
 * series the user is most likely to care about.
 */
export function graphSlotForIndex(index: number): GraphSlot {
    const n = GRAPH_SLOTS.length;
    return GRAPH_SLOTS[(n - 1 - (index % n)) % n];
}

/**
 * Build a stable `id → GraphSlot` mapping from an ordered list. Pass the
 * same list order everywhere that reads colors so an item doesn't change
 * color between views.
 */
export function buildColorMap<T extends { uuid: string }>(items: readonly T[]): Record<string, GraphSlot> {
    const map: Record<string, GraphSlot> = {};
    items.forEach((item, i) => {
        map[item.uuid] = graphSlotForIndex(i);
    });
    return map;
}

/**
 * Alias of {@link buildColorMap} kept for the YaaS-client reporting call
 * sites that read in terms of "positions." Same behavior — the generic
 * just requires a `uuid` field.
 */
export const buildPositionColorMap = buildColorMap;
